import { useRef, useEffect } from 'react';

import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { CONFLUENCE_RECAP_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

const useShowRecap2025QueryParam = createSingleQueryParamHook('showRecap2025');

/**
 * Hook to determine if the Recap Publish Spotlight is eligible to show.
 *
 * The spotlight is eligible when:
 * - Query param `showRecap2025` equals "open"
 * - The page is not a live page
 * - The content is an unpublished draft
 * - The user has not seen the spotlight before
 *
 *
 * @param isUnpublishedDraft - Whether the current content is an unpublished draft
 * @returns Whether the spotlight is eligible to show
 */
export const useRecapSpotlightEligibility = (isUnpublishedDraft: boolean): boolean => {
	const hasSeenPublishSpotlightRef = useRef<string | null>(null);

	const showRecap2025QueryParam = useShowRecap2025QueryParam();
	const isLivePage = useIsCurrentPageLive();

	const isEligible = showRecap2025QueryParam === 'open' && !isLivePage && isUnpublishedDraft;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		CONFLUENCE_RECAP_ONBOARDING_STATE_KEYS.HAS_SEEN_PUBLISH_SPOTLIGHT,
		!isEligible,
	);
	const { hasSeenPublishSpotlight } = deserializeState(onboardingStateData);

	// Mark GraphQL errors as handled since we gracefully treat them as "hasn't seen spotlight"
	useEffect(() => {
		if (onboardingStateError) {
			markErrorAsHandled(onboardingStateError);
		}
	}, [onboardingStateError]);

	// Capture the value once when it first becomes available
	// This ensures state changes during the current render cycle don't affect eligibility
	if (hasSeenPublishSpotlightRef.current === null && !onboardingStateLoading) {
		hasSeenPublishSpotlightRef.current = hasSeenPublishSpotlight;
	}

	if (onboardingStateLoading || onboardingStateError) {
		return false;
	}

	// If user has already seen the spotlight, don't show it again
	if (Boolean(hasSeenPublishSpotlightRef.current)) {
		return false;
	}

	return isEligible;
};
